import { AppDispatch } from '../store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '../config';
import { prepareHeaders } from './helpers';
import { SERVICE_API_TAG_TYPES } from '../constants';
import { createBaseQueryWithReauth } from './baseQueryWithReauth';

const createSemaphore = (max: number) => {
  let active = 0;
  const queue: (() => void)[] = [];

  const next = () => {
    if (active >= max || queue.length === 0) {
      return;
    }

    active++;
    queue.shift()?.();
  };

  return async <T>(fn: () => Promise<T>): Promise<T> =>
    new Promise((resolve, reject) => {
      queue.push(async () => {
        try {
          resolve(await fn());
        } catch (error) {
          reject(error);
        } finally {
          active--;
          next();
        }
      });

      next();
    });
};

const limit = createSemaphore(config.maxRequestsLimit);

const baseQuery = createBaseQueryWithReauth(
  fetchBaseQuery({ baseUrl: config.assetServiceBaseUrl, prepareHeaders: prepareHeaders }),
);

const baseApi = createApi({
  reducerPath: 'baseApi',
  keepUnusedDataFor: config.rtkCacheTimeout,
  // baseQuery: baseQuery,
  baseQuery: (args, api, extraOptions) => limit(() => Promise.resolve(baseQuery(args, api, extraOptions))),
  tagTypes: SERVICE_API_TAG_TYPES,
  endpoints: () => ({}),
});

interface BaseApiCustomHelpers {
  invalidateAllTags: () => (dispatch: AppDispatch) => ReturnType<typeof baseApi.util.invalidateTags>;
}

type BaseApiWithCustom = typeof baseApi & { custom: BaseApiCustomHelpers };

const baseApiWithCustom = baseApi as BaseApiWithCustom;

baseApiWithCustom.custom = {
  invalidateAllTags: () => (dispatch: AppDispatch) =>
    dispatch(baseApi.util.invalidateTags(SERVICE_API_TAG_TYPES as any)),
};

export { baseApiWithCustom as baseApi };
export const resetApiState = baseApi.util.resetApiState;
